/**
 * Helpers for admin listing reference codes and date-based sorting.
 *
 * Reference format: {COUNTRY}-[{REGION_SHORT}-]{LOCATION_SHORT}-{SHORT_ID}
 * Example: DE-KAIS-A3F82B1C
 *          DE-RHEI-KAIS-A3F82B1C  (when region is present)
 *
 * The short ID is derived from the listing UUID and guarantees uniqueness even
 * when country/location segments match.
 *
 * No private data (owner email, name, payment info, identity fields) is included.
 */

export const SORT_ORDERS = ['newest', 'oldest', 'waiting'];

/** Uppercase the first N alphanumeric chars of a string, or return fallback. */
function safeSlug(raw, maxLen, fallback) {
  const cleaned = String(raw || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return cleaned.slice(0, maxLen) || fallback;
}

/**
 * Derive a short, uppercase location slug from a free-text location name.
 * Takes the first word (split on whitespace, comma, slash, or dash) and
 * keeps up to 6 uppercase alphanumeric characters.
 */
function locationSlug(name) {
  if (!name || !String(name).trim()) return 'CITY';
  const firstWord = String(name).trim().split(/[\s,\/\-]/)[0];
  return safeSlug(firstWord, 4, 'CITY');
}

/**
 * Derive a short region slug — up to 4 chars of the first word.
 */
function regionSlug(region) {
  if (!region || !String(region).trim()) return null;
  const firstWord = String(region).trim().split(/[\s,\/\-]/)[0];
  const slug = safeSlug(firstWord, 4, '');
  return slug || null;
}

/**
 * Derive an 8-char uppercase hex ID from a listing UUID.
 * Strips hyphens, uppercases, takes first 8 chars.
 */
function shortId(id) {
  const cleaned = String(id || '').replace(/-/g, '').toUpperCase();
  return cleaned.slice(0, 8) || '00000000';
}

/**
 * Build a unique, human-readable listing reference code.
 *
 * Format: {COUNTRY}-[{REGION}-]{LOCATION}-{SHORT_ID}
 * Only uses: country_code, region, location_name, id
 * Never uses: owner_email, owner_name, payment data, identity fields
 */
export function buildListingReference(listing) {
  const country = safeSlug(listing?.country_code, 4, 'XX');
  const loc = locationSlug(listing?.location_name);
  const reg = regionSlug(listing?.region);
  const idSuffix = shortId(listing?.id);

  const parts = [country];
  if (reg) parts.push(reg);
  parts.push(loc);
  parts.push(idSuffix);

  return parts.join('-');
}

/**
 * Returns the Unix timestamp (ms) for a listing's creation date.
 * Returns 0 if created_at is absent or unparseable.
 */
export function getListingCreatedAt(listing) {
  const v = listing?.created_at;
  if (!v) return 0;
  const ts = new Date(v).getTime();
  return isNaN(ts) ? 0 : ts;
}

/**
 * Returns the Unix timestamp (ms) for when a listing entered the review queue.
 * Preference order: last_resubmitted_at → resubmitted_for_review_at →
 *                   submitted_for_review_at → review_received_at → created_at
 * Returns 0 if no usable date is found.
 */
export function getListingSubmissionDate(listing) {
  const candidates = [
    listing?.last_resubmitted_at,
    listing?.resubmitted_for_review_at,
    listing?.submitted_for_review_at,
    listing?.review_received_at,
    listing?.created_at,
  ];
  for (const v of candidates) {
    if (!v) continue;
    const ts = new Date(v).getTime();
    if (!isNaN(ts)) return ts;
  }
  return 0;
}

/**
 * Sort a listings array by the given sortOrder without mutating the input.
 *
 * @param {object[]} listings
 * @param {'newest'|'oldest'|'waiting'|string} sortOrder
 * @returns {object[]}
 */
export function sortListingsByOrder(listings, sortOrder) {
  const arr = [...listings];
  switch (sortOrder) {
    case 'newest':
      arr.sort((a, b) => getListingCreatedAt(b) - getListingCreatedAt(a));
      break;
    case 'oldest':
      arr.sort((a, b) => getListingCreatedAt(a) - getListingCreatedAt(b));
      break;
    case 'waiting':
      arr.sort((a, b) => getListingSubmissionDate(a) - getListingSubmissionDate(b));
      break;
    default:
      break;
  }
  return arr;
}
